<template>
    <div class="ps-correction-root">
        <div v-if="loading" class="ps-state ps-state--loading">
            Loading sheet…
        </div>

        <div v-else-if="loadError" class="ps-state ps-state--error">
            <strong>Could not load sheet.</strong>
            <div class="ps-state__detail">{{ loadError }}</div>
        </div>

        <div v-else class="ps-correction">
            <header class="ps-correction__header">
                <h2 class="ps-correction__title">{{ sheet.assessmentName || 'Answer Sheet' }}</h2>
                <div class="ps-correction__sub">
                    Page {{ sheet.pageNo }}<span v-if="sheet.totalPages"> of {{ sheet.totalPages }}</span>
                </div>
                <div v-if="sheet.errorMessage" class="ps-correction__sub-warn">
                    {{ sheet.errorMessage }}
                </div>
            </header>

            <div v-if="imageUrl" class="ps-correction__image-wrap">
                <img :src="imageUrl" alt="Scanned answer sheet" class="ps-correction__image" />
            </div>

            <section v-if="studentIdInputVisible" class="ps-correction__section">
                <label class="ps-correction__label" :for="studentInputId">Student ID</label>
                <input
                    :id="studentInputId"
                    v-model="editedStudentId"
                    type="text"
                    class="ps-correction__input"
                    autocomplete="off"
                    spellcheck="false"
                    :placeholder="studentIdPlaceholder"
                />
                <div class="ps-correction__hint">
                    {{ originalStudentIdHint }}
                </div>
            </section>

            <section v-else-if="originalStudentId" class="ps-correction__section">
                <div class="ps-correction__label">Student ID</div>
                <div class="ps-correction__readonly-value">{{ originalStudentId }}</div>
                <div class="ps-correction__hint">
                    This sheet was generated for a specific student — the ID cannot be edited here.
                </div>
            </section>

            <section v-if="questionsVisible" class="ps-correction__section">
                <h3 class="ps-correction__section-title">Answers</h3>
                <div v-if="!normalizedQuestions.length" class="ps-correction__empty">
                    No questions returned for this sheet.
                </div>
                <ul v-else class="ps-correction__questions">
                    <li
                        v-for="q in normalizedQuestions"
                        :key="q.qid"
                        class="ps-question"
                        :class="{
                            'ps-question--multi-attempt': q.isMultiAttemptError,
                            'ps-question--partial': q.isPartialError,
                        }"
                    >
                        <div class="ps-question__head">
                            <span class="ps-question__label"
                                >{{ q.label || q.qid }}<span v-if="q.typeName" class="ps-question__type-name">. {{ q.typeName }}</span></span>
                            <span v-if="q.permutation" class="ps-question__hint">(one per row &amp; one per column)</span>
                            <span v-if="q.isMultiAttemptError" class="ps-question__flag">multiple marked</span>
                            <span v-else-if="q.isPartialError" class="ps-question__flag">partially marked</span>
                        </div>
                        <table v-if="q.editable && q.grid && q.gridOrientation === 'rows'" class="ps-question__grid">
                            <tbody>
                                <tr v-for="(row, rowIndex) in q.grid" :key="rowIndex">
                                    <th class="ps-question__grid-row-label">{{ rowLetter(rowIndex) }}</th>
                                    <td v-for="(b, colIndex) in row" :key="b.ansid">
                                        <button
                                            type="button"
                                            class="ps-bubble"
                                            :class="{ 'ps-bubble--on': isSelected(q.qid, b.ansid) }"
                                            @click="toggleBubble(q, b, rowIndex, colIndex)"
                                        >
                                            {{ b.text || b.ansid }}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-else-if="q.editable && q.grid && q.gridOrientation === 'columns'" class="ps-question__numeric-grid">
                            <div
                                v-for="(col, colIndex) in q.grid"
                                :key="colIndex"
                                class="ps-question__numeric-col"
                            >
                                <button
                                    v-for="b in col"
                                    :key="b.ansid"
                                    type="button"
                                    class="ps-bubble ps-bubble--narrow"
                                    :class="{ 'ps-bubble--on': isSelected(q.qid, b.ansid) }"
                                    @click="toggleBubble(q, b, colIndex)"
                                >
                                    {{ b.text || b.ansid }}
                                </button>
                            </div>
                        </div>
                        <div v-else-if="q.editable" class="ps-question__bubbles">
                            <button
                                v-for="b in q.bubbles"
                                :key="b.ansid"
                                type="button"
                                class="ps-bubble"
                                :class="{ 'ps-bubble--on': isSelected(q.qid, b.ansid) }"
                                @click="toggleBubble(q, b)"
                            >
                                {{ b.text || b.ansid }}
                            </button>
                        </div>
                        <div v-else class="ps-question__readonly">
                            <em>{{ q.readonlyReason }}</em>
                        </div>
                    </li>
                </ul>
            </section>

            <section v-else-if="questionsHiddenForMissingId" class="ps-correction__section">
                <div class="ps-correction__notice">
                    Question answers can't be reviewed until the student ID is identified.
                    Enter the correct ID above and submit — the sheet will be re-processed,
                    and bubble corrections will become available on your next visit.
                </div>
            </section>

            <footer class="ps-correction__footer">
                <div v-if="submitMessage" class="ps-correction__submit-msg" :class="submitMessageClass">
                    {{ submitMessage }}
                </div>
                <button
                    type="button"
                    class="ps-btn ps-btn--primary"
                    :disabled="submitting || !anythingToSubmit"
                    @click="handleSubmit"
                >
                    {{ submitting ? 'Submitting…' : submitButtonLabel }}
                </button>
            </footer>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { normalizeQuestions } from './utils.js';

const props = defineProps({
    // The sheet data the embed renders. Either a plain object (already
    // fetched by the host) or a Promise that resolves to one. The embed
    // does not fetch anything itself — auth + transport are the host's
    // responsibility.
    data: { type: [Object, Promise], required: true },
    // Async callback invoked when the user submits. Receives the
    // corrected payload `{ student_id, questions }` and is expected to
    // return a Promise. The embed shows a "Submitting…" state while
    // it's pending and surfaces the resolved value's `message` (or a
    // thrown error's message) in the footer.
    onSubmit: { type: Function, required: true },
    // Whether to show the editable student-ID input.
    //   'auto' (default): hide when the sheet is pre-filled
    //     (template_id === 0, i.e. unique-per-student), show otherwise.
    //   true / false: explicit override.
    showStudentIdInput: { type: [String, Boolean], default: 'auto' },
    // Whether to show the question/answer correction grid.
    //   'auto' (default): hide when the bubbled-in student ID is
    //     missing/zero — the engine can't produce question data until
    //     the sheet is re-processed with a real student ID. Show
    //     otherwise.
    //   true / false: explicit override.
    showQuestions: { type: [String, Boolean], default: 'auto' },
});

const loading = ref(true);
const loadError = ref(null);
const submitting = ref(false);
const submitMessage = ref(null);
const submitOk = ref(false);

const sheet = ref({
    assessmentName: '',
    email: '',
    pageNo: 1,
    totalPages: 1,
    templateId: '',
    imageUrl: '',
    taskId: '',
    errorMessage: '',
    questions: [],
});

const originalStudentId = ref('');
const editedStudentId = ref('');
const editedAnswers = reactive({});

const studentInputId = `ps-student-${Math.random().toString(36).slice(2, 9)}`;
const studentIdPlaceholder = computed(() =>
    originalStudentId.value ? '' : 'Enter student ID for this sheet'
);
const originalStudentIdHint = computed(() => {
    const original = originalStudentId.value.trim();
    if (!original) return 'No student ID was read from this sheet.';
    if (editedStudentId.value === original) return `Original: ${original}`;
    return `Original: ${original} → corrected to "${editedStudentId.value || '(empty)'}"`;
});

const imageUrl = computed(() => sheet.value.imageUrl || '');

const submitMessageClass = computed(() => ({
    'ps-correction__submit-msg--ok': submitOk.value,
    'ps-correction__submit-msg--err': !submitOk.value,
}));

const normalizedQuestions = computed(() => normalizeQuestions(sheet.value.questions));

// Treat the engine's "no student matched" sentinels as missing.
const studentIdResolved = computed(() => {
    const sid = originalStudentId.value.trim();
    return sid !== '' && sid !== '0';
});

const isPrefilledSheet = computed(() => String(sheet.value.templateId ?? '') === '0');

const studentIdInputVisible = computed(() => {
    if (props.showStudentIdInput === true || props.showStudentIdInput === false) {
        return props.showStudentIdInput;
    }
    // 'auto': pre-filled (unique-per-student) sheets have the ID baked in.
    return ! isPrefilledSheet.value;
});

const questionsVisible = computed(() => {
    if (props.showQuestions === true || props.showQuestions === false) {
        return props.showQuestions;
    }
    // 'auto': engine doesn't produce question data until a real student
    // is resolved. Hide the grid and prompt for ID fix-up first.
    return studentIdResolved.value;
});

const questionsHiddenForMissingId = computed(
    () => ! questionsVisible.value && ! studentIdResolved.value
);

const anythingToSubmit = computed(() => studentIdInputVisible.value || questionsVisible.value);

const submitButtonLabel = computed(() => {
    if (studentIdInputVisible.value && ! questionsVisible.value) {
        return 'Submit student ID';
    }
    if (questionsVisible.value && ! studentIdInputVisible.value) {
        return 'Submit answer corrections';
    }
    return 'Submit correction';
});

onMounted(initialize);

async function initialize() {
    loading.value = true;
    loadError.value = null;

    try {
        // Accept either a plain object or a Promise — host-side fetch
        // patterns differ, and we want both to be one-liners.
        const data = await Promise.resolve(props.data);
        applySheetData(data || {});
    } catch (err) {
        loadError.value = err && err.message ? err.message : String(err);
    } finally {
        loading.value = false;
    }
}

function applySheetData(data) {
    // Accept both camelCase (engine response) and snake_case (some
    // host APIs normalize) so the host doesn't have to translate.
    sheet.value = {
        assessmentName: data.assessmentName || data.assessment_name || '',
        email: data.email || '',
        pageNo: parseInt(data.pageNo ?? data.page_no ?? 1, 10),
        totalPages: parseInt(data.totalPages || data.total_pages || 1, 10),
        templateId: String(data.templateId ?? data.template_id ?? ''),
        imageUrl: data.imageUrl || data.image_url || '',
        taskId: String(data.taskId ?? data.task_id ?? ''),
        errorMessage: data.errorMessage || data.error_message || '',
        questions: data.questions || {},
    };

    const serverStudentId = String(data.studentId ?? data.student_id ?? '');
    originalStudentId.value = serverStudentId;
    editedStudentId.value = serverStudentId;

    seedEditedAnswers(normalizedQuestions.value);
}

function seedEditedAnswers(questions) {
    for (const q of questions) {
        if (!q.editable || !q.qid) continue;
        const initial = q.bubbles.filter((b) => b.marked).map((b) => b.ansid);
        editedAnswers[q.qid] = {
            qType: q.qType,
            isMulti: q.isMulti,
            selected: new Set(initial),
        };
    }
}

function isSelected(qid, ansid) {
    const e = editedAnswers[qid];
    return !!(e && e.selected.has(ansid));
}

function toggleBubble(q, b, groupIndex = null, colIndex = null) {
    let entry = editedAnswers[q.qid];
    if (!entry) {
        entry = { qType: q.qType, isMulti: q.isMulti, selected: new Set() };
        editedAnswers[q.qid] = entry;
    }

    if (entry.selected.has(b.ansid)) {
        // Toggle off — always allowed, no constraint propagation.
        entry.selected.delete(b.ansid);
        return;
    }

    if (q.grid && groupIndex !== null) {
        // Grid types: clicking a bubble can clear neighbors depending
        // on the type's constraint rules.
        if (q.perGroupSingleSelect) {
            // Clear the row (or the column, for numeric where the
            // "group" is a column) before adding the new selection.
            for (const gb of q.grid[groupIndex] || []) {
                entry.selected.delete(gb.ansid);
            }
        }
        if (q.permutation && colIndex !== null) {
            // Ordering: each column gets used at most once across
            // rows. Clear the bubble at the same colIndex in every
            // other row before adding the new selection.
            for (const row of q.grid) {
                if (row[colIndex]) {
                    entry.selected.delete(row[colIndex].ansid);
                }
            }
        }
    } else if (!entry.isMulti) {
        // Flat single-select (types 1, 4): clear all and add one.
        entry.selected.clear();
    }

    entry.selected.add(b.ansid);
}

/**
 * Row label for grid-rendered types (5 matching, 6 ordering). Engine
 * emits rows in order; we display them as A, B, C, … matching the
 * printed sheet layout. Cycles to AA, AB, … if a question ever has
 * more than 26 rows (unlikely for the editable types).
 */
function rowLetter(index) {
    if (index < 26) {
        return String.fromCharCode(65 + index);
    }
    const head = String.fromCharCode(65 + Math.floor(index / 26) - 1);
    const tail = String.fromCharCode(65 + (index % 26));
    return head + tail;
}

// Engine wants integer ids when the string is purely numeric, but we
// don't want to silently corrupt non-numeric ids (some legacy data uses
// strings). Convert when safe, pass through otherwise.
function toIntOrString(value) {
    const n = String(value ?? '').trim();
    if (n === '') return n;
    return /^-?\d+$/.test(n) ? parseInt(n, 10) : n;
}

async function handleSubmit() {
    submitting.value = true;
    submitMessage.value = null;
    submitOk.value = false;

    try {
        // Engine submit shape (snake_case, integer ids). One entry per
        // question the user actually marked; bubbles map 1:1 to engine
        // answer_ids. meta_data is carried through verbatim for ordering
        // questions (engine ignores it for other types).
        const questionsPayload = [];
        if (questionsVisible.value) {
            for (const q of normalizedQuestions.value) {
                if (!q.editable || !q.qid) continue;
                const entry = editedAnswers[q.qid];
                if (!entry || entry.selected.size === 0) continue;

                const answers = q.bubbles
                    .filter((b) => entry.selected.has(b.ansid))
                    .map((b) => {
                        const answer = { answer_id: toIntOrString(b.ansid) };
                        if (b.metaData) answer.meta_data = b.metaData;
                        return answer;
                    });

                if (!answers.length) continue;

                const item = {
                    question_id: toIntOrString(q.qid),
                    question_type: q.questionType,
                    marked_answer_text: '',
                    answers,
                };
                // Engine expects meta_data only for ordering (type 6).
                // Other types (cloze, MCQ, …) sometimes carry a
                // questionMetaData blob from Learnosity that the engine
                // rejects (its QuestionCorrection.meta_data DTO is typed
                // for the ordering case only).
                if (q.questionType === 6 && q.questionMetaData) {
                    item.meta_data = q.questionMetaData;
                }

                questionsPayload.push(item);
            }
        }

        // Hand the corrected payload to the host. Only include the
        // fields the user could actually edit — engine semantics treat
        // an omitted field as "leave as-is," and an empty/zero field
        // would either be rejected or unintentionally reassigned.
        // The host's onSubmit is responsible for transport, auth, and
        // side effects; the embed only cares about the outcome.
        const submitBody = {};
        if (studentIdInputVisible.value) {
            submitBody.student_id = editedStudentId.value;
        }
        if (questionsVisible.value) {
            submitBody.questions = questionsPayload;
        }

        const result = await props.onSubmit(submitBody);

        submitOk.value = true;
        submitMessage.value = (result && result.message) || 'Correction submitted.';
    } catch (err) {
        submitOk.value = false;
        submitMessage.value = err && err.message ? err.message : String(err);
    } finally {
        submitting.value = false;
    }
}
</script>

<style scoped>
.ps-correction-root {
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: #1f2937;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    max-width: 720px;
    box-sizing: border-box;
    line-height: 1.4;
}

.ps-correction-root *,
.ps-correction-root *::before,
.ps-correction-root *::after {
    box-sizing: inherit;
}

.ps-state {
    padding: 16px;
    text-align: center;
    color: #6b7280;
}

.ps-state--error {
    color: #b91c1c;
}

.ps-state__detail {
    font-size: 13px;
    margin-top: 4px;
    color: #7f1d1d;
}

.ps-correction__header {
    margin-bottom: 12px;
}

.ps-correction__title {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 2px 0;
}

.ps-correction__sub {
    font-size: 13px;
    color: #6b7280;
}

.ps-correction__sub-warn {
    font-size: 12px;
    color: #b45309;
    margin-top: 4px;
}

.ps-correction__image-wrap {
    margin-bottom: 16px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
    text-align: center;
}

.ps-correction__image {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto;
}

.ps-correction__section {
    margin-bottom: 16px;
}

.ps-correction__section-title {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #374151;
}

.ps-correction__label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 4px;
    color: #374151;
}

.ps-correction__input {
    width: 100%;
    padding: 8px 10px;
    font-size: 14px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #ffffff;
    color: #111827;
}

.ps-correction__input:focus {
    outline: 2px solid #2563eb;
    outline-offset: -1px;
    border-color: #2563eb;
}

.ps-correction__readonly-value {
    padding: 8px 10px;
    font-size: 14px;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background: #f9fafb;
    color: #111827;
}

.ps-correction__notice {
    padding: 12px;
    font-size: 13px;
    border: 1px solid #fcd34d;
    border-radius: 4px;
    background: #fffbeb;
    color: #78350f;
    line-height: 1.5;
}

.ps-correction__hint {
    font-size: 12px;
    color: #6b7280;
    margin-top: 4px;
}

.ps-correction__empty {
    font-size: 13px;
    color: #6b7280;
    font-style: italic;
}

.ps-correction__questions {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ps-question {
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 10px 12px;
}

.ps-question--multi-attempt {
    border-color: #f59e0b;
    background: #fffbeb;
}

.ps-question--partial {
    border-color: #fbbf24;
    background: #fefce8;
}

.ps-question__head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
}

.ps-question__label {
    font-weight: 600;
    font-size: 13px;
}

.ps-question__hint {
    font-size: 12px;
    color: #6b7280;
    font-weight: 400;
}

.ps-question__type-name {
    font-weight: 400;
    color: #6b7280;
}

.ps-question__flag {
    margin-left: auto;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #92400e;
    background: #fef3c7;
    padding: 2px 6px;
    border-radius: 3px;
    white-space: nowrap;
}

.ps-question__bubbles {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.ps-question__readonly {
    font-size: 12px;
    color: #6b7280;
}

/* Row × column grid: matching (type 5) and ordering (type 6). One bubble
   per row enforced by toggleBubble's perGroupSingleSelect path. */
.ps-question__grid {
    border-collapse: collapse;
    margin-top: 4px;
}
.ps-question__grid tbody tr + tr {
    border-top: 1px solid transparent;
}
.ps-question__grid th,
.ps-question__grid td {
    padding: 3px 4px;
    text-align: center;
    vertical-align: middle;
}
.ps-question__grid-row-label {
    font-weight: 600;
    font-size: 13px;
    color: #374151;
    padding-right: 10px !important;
    text-align: right;
    min-width: 24px;
}

/* Column grid: numeric fill-in (type 3). Each "group" is a column of
   digit/operator bubbles; columns sit side-by-side like the printed
   sheet's digit positions. */
.ps-question__numeric-grid {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    margin-top: 4px;
    overflow-x: auto;
}
.ps-question__numeric-col {
    display: flex;
    flex-direction: column;
    gap: 3px;
    flex: 0 0 auto;
}
.ps-bubble--narrow {
    min-width: 30px;
    padding: 4px 8px;
    font-size: 13px;
}

.ps-bubble {
    min-width: 36px;
    padding: 6px 12px;
    font-size: 14px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #ffffff;
    color: #1f2937;
    cursor: pointer;
    font-family: inherit;
}

.ps-bubble:hover {
    background: #f3f4f6;
}

.ps-bubble--on {
    background: #2563eb;
    color: #ffffff;
    border-color: #1d4ed8;
}

.ps-bubble--on:hover {
    background: #1d4ed8;
}

.ps-correction__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
}

.ps-correction__submit-msg {
    flex: 1;
    font-size: 13px;
}

.ps-correction__submit-msg--ok {
    color: #166534;
}

.ps-correction__submit-msg--err {
    color: #b91c1c;
}

.ps-btn {
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 4px;
    cursor: pointer;
    font-family: inherit;
    border: 1px solid transparent;
}

.ps-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.ps-btn--primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #1d4ed8;
}

.ps-btn--primary:hover:not(:disabled) {
    background: #1d4ed8;
}
</style>
